// ProfileCardSmall.tsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  MapPin,
  Heart,
  Share2,
  Eye,
  User,
  Zap,
  BookOpen,
  MessageCircle,
  CheckCircle,
  Star
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { BoyIcon, CartoonFemaleIcon, CartoonMaleIcon, FemaleIcon, GirlIcon, MaleIcon } from './profileHeplers/GenderSVG';
import myIcons from '@/assets/Icons';

interface Profile {
  user_id: string;
  nickname: string;
  age: number;
  gender: string;
  city: string;
  country: string;
  bio?: string;
  interests?: string[];
  photos?: string[];
  is_active?: boolean;
  verified?: boolean;
  premium?: boolean;
  profile_views?: number;
  likes_count?: number;
  my_reaction_type?: string;
}

interface ProfileCardSmallProps {
  profile: Profile;
  onLike?: (profile: Profile) => void;
  onSuperLike?: (profile: Profile) => void;
  onSave?: (profile: Profile) => void;
  onMessage?: (profile: Profile) => void;
  onShare?: (profile: Profile) => void;
  onClick?: (profile: Profile) => void;
  showActions?: boolean;
  size?: 'sm' | 'md';
}

export default function ProfileCardSmall({ 
  profile, 
  onLike, 
  onSuperLike, 
  onSave, 
  onMessage, 
  onShare,
  onClick,
  showActions = true,
  size = 'md'
}: ProfileCardSmallProps) {
  const [isLiked, setIsLiked] = useState(profile?.my_reaction_type === 'like');
  const [isSaved, setIsSaved] = useState(profile?.my_reaction_type === 'save');
  const [isSuperLiked, setIsSuperLiked] = useState(profile?.my_reaction_type === 'super_like');
  const [isHovered, setIsHovered] = useState(false);

  const isMale = profile?.gender === "m" || profile?.gender === "male";
  const displayName = profile?.nickname || "Anonymous";
  const locationDisplay = profile?.city 
    ? (profile?.country ? `${profile?.city}, ${profile?.country}` : profile?.city)
    : "Location not specified";

  const sizeClasses = {
    sm: {
      container: 'p-3',
      avatar: 'w-16 h-16',
      title: 'text-sm',
      location: 'text-xs',
      actions: 'gap-1',
      button: 'p-1.5'
    },
    md: {
      container: 'p-4',
      avatar: 'w-20 h-20',
      title: 'text-base',
      location: 'text-xs',
      actions: 'gap-2',
      button: 'p-2'
    }
  };

  const currentSize = sizeClasses[size];

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLiked(!isLiked);
    onLike?.(profile);
  };

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSaved(!isSaved);
    onSave?.(profile);
  };

  const handleSuperLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSuperLiked(!isSuperLiked);
    onSuperLike?.(profile);
  };

  const handleMessage = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMessage?.(profile);
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onShare) {
      onShare(profile);
    } else {
      const url = `${window.location.origin}/profile/${profile?.user_id}`;
      try {
        if (navigator.share) {
          await navigator.share({
            title: `${displayName}, ${profile?.age}`,
            text: `Check out ${displayName}'s profile`,
            url: url,
          });
        } else {
          await navigator.clipboard.writeText(url);
          alert("Profile link copied!");
        }
      } catch (err) {
        console.error("Error sharing:", err);
      }
    }
  };

  const handleClick = () => {
    onClick?.(profile);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -2, scale: 1.02 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={handleClick}
      className={`group bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg dark:hover:shadow-gray-900/30 transition-all cursor-pointer ${currentSize.container}`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar Section */}
        <div className="relative flex-shrink-0">
          <div className={`${currentSize.avatar} rounded-full overflow-hidden bg-gradient-to-br ${
            isMale 
              ? "from-indigo-100 to-indigo-200 dark:from-indigo-900/30 dark:to-indigo-800/30" 
              : "from-pink-100 to-pink-200 dark:from-pink-900/30 dark:to-pink-800/30"
          }`}>
            {profile?.photos && profile.photos.length > 0 ? (
              <img 
                src={profile.photos[0]} 
                alt={displayName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                {isMale ? (
        <img src={myIcons.man} className='h-full object-cover' />
      ) : (
        <img src={myIcons.woman} className='h-full object-cover'/>
      )}

              </div>
            )}
          </div>

          {/* Online Status */}
          {profile?.is_active && (
            <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full ring-2 ring-white dark:ring-gray-800"></div>
          )}

          {/* Verified Badge */}
          {profile?.verified && (
            <div className="absolute -top-1 -right-1">
              <CheckCircle className="w-4 h-4 text-blue-500 fill-white dark:fill-gray-800" />
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <h3 className={`font-semibold text-gray-900 dark:text-white ${currentSize.title}`}>
                  {displayName}
                </h3>
                <span className="text-gray-500 dark:text-gray-400 text-xs">
                  {profile?.age}
                </span>
                {profile?.premium && (
                  <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                )}
              </div>
              
              <div className="flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3 text-gray-400" />
                <span className={`text-gray-500 dark:text-gray-400 ${currentSize.location} truncate max-w-[120px]`}>
                  {locationDisplay}
                </span>
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              {profile?.profile_views !== undefined && profile.profile_views > 0 && (
                <div className="flex items-center gap-0.5" title="Profile views">
                  <Eye className="w-3 h-3" />
                  <span>{profile.profile_views}</span>
                </div>
              )}
              {profile?.likes_count !== undefined && profile.likes_count > 0 && (
                <div className="flex items-center gap-0.5" title="Likes">
                  <Heart className="w-3 h-3" />
                  <span>{profile.likes_count}</span>
                </div>
              )}
            </div>
          </div>

          {/* Bio Preview */}
          {profile?.bio && size === 'md' && (
            <p className="text-xs text-gray-600 dark:text-gray-300 mt-1.5 line-clamp-1">
              {profile.bio.substring(0, 60)}...
            </p>
          )}

          {/* Interests (compact) */}
          {profile?.interests && profile.interests.length > 0 && size === 'md' && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {profile.interests.slice(0, 2).map((interest) => (
                <span key={interest} className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded-full">
                  {interest}
                </span>
              ))}
              {profile.interests.length > 2 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  +{profile.interests.length - 2}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      {showActions && (
        <div className={`flex items-center justify-between mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 ${currentSize.actions}`}>
          <div className="flex gap-1">
            {/* Like Button */}
            <button
              onClick={handleLike}
              className={`rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all ${currentSize.button}`}
              title={isLiked ? "Unlike" : "Like"}
            >
              <Heart 
                className={`w-4 h-4 transition-all ${
                  isLiked 
                    ? "fill-red-500 text-red-500" 
                    : "text-gray-400 hover:text-red-500"
                }`} 
              />
            </button>

            {/* Super Like Button */}
            <button
              onClick={handleSuperLike}
              className={`rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all ${currentSize.button}`}
              title={isSuperLiked ? "Remove Super Like" : "Super Like"}
            >
              <Zap 
                className={`w-4 h-4 transition-all ${
                  isSuperLiked 
                    ? "fill-yellow-500 text-yellow-500" 
                    : "text-gray-400 hover:text-yellow-500"
                }`} 
              />
            </button>

            {/* Save Button */}
            <button
              onClick={handleSave}
              className={`rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all ${currentSize.button}`}
              title={isSaved ? "Unsave" : "Save"}
            >
              <BookOpen 
                className={`w-4 h-4 transition-all ${
                  isSaved 
                    ? "fill-blue-500 text-blue-500" 
                    : "text-gray-400 hover:text-blue-500"
                }`} 
              />
            </button>

            {/* Message Button */}
            <button
              onClick={handleMessage}
              className={`rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all ${currentSize.button}`}
              title="Send Message"
            >
              <MessageCircle className="w-4 h-4 text-gray-400 hover:text-purple-500" />
            </button>
          </div>

          {/* Share Button */}
          <button
            onClick={handleShare}
            className={`rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all ${currentSize.button}`}
            title="Share Profile"
          >
            <Share2 className="w-4 h-4 text-gray-400 hover:text-purple-500" />
          </button>
        </div>
      )}
    </motion.div>
  );
}